import React from 'react';
import { SectionTitle } from '@/components/ui/shared/section-title';
import { getMediaData, resizedImageProps } from '@/lib/utils/media.utils';
import type { Media } from '@/types';
import { LandscapeSpecifications } from './specifications';

interface Specification {
  value: string | number | { id: number; title: string; updatedAt?: string; createdAt?: string } | null;
  light?: boolean;
  url?: string;
}

export interface EntrySpec {
  label: string;
  specifications: Specification[];
}

/**
 * An entry's facts, for a page whose layout has no description block to carry them: the same
 * card as that block, with the facts filling it as two lists side by side. With no facts,
 * nothing is drawn. The entry's words are not repeated here; its header shows them in full.
 */
export function EntryInformation({ specs }: { specs: EntrySpec[] }) {
  if (!specs.length) return null;
  const split = specs.length > 1 ? Math.ceil(specs.length / 2) : specs.length;
  return (
    <div className="content-section rounded-md mb-[12px]">
      <div className="pt-[15px] w-full px-[15px] rounded-md">
        <div className="font-mono text-[10px] pt-2 pb-12">
          <SectionTitle title="INFORMATION" className="mb-5" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <LandscapeSpecifications specs={specs.slice(0, split)} />
            {split < specs.length ? <LandscapeSpecifications specs={specs.slice(split)} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The layout blocks that show pictures or film. A page with none of them has no media of its own. */
const MEDIA_BLOCKS = new Set([
  'LandscapeGallery',
  'LandscapeDisplay',
  'LandscapeVisionDisplay',
  'LandscapeChapter',
  'LandscapeChapterHero',
  'LandscapeDocumentary',
  'LandscapeRenders',
  'LandscapeEmbed',
  'LandscapeImmersive',
]);

/** What a page needs to know about its own layout to fill the gaps: is there a description block, is there any media. */
export function readLayout(layout?: { blockType: string }[] | null) {
  const blocks = layout ?? [];
  return {
    describes: blocks.some((block) => block.blockType === 'LandscapeDescription'),
    showsMedia: blocks.some((block) => MEDIA_BLOCKS.has(block.blockType)),
  };
}

/**
 * An entry's own photographs, for a page whose layout shows no pictures or film at all:
 * rounded frames on the black page, three across, like the gallery block's.
 */
export function EntryPhotos({ photos, title }: { photos?: (number | Media)[] | null; title: string }) {
  const frames = (photos ?? []).map((photo) => getMediaData(typeof photo === 'number' ? null : photo)).filter((photo) => photo.url);
  if (!frames.length) return null;
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[12px] mb-[12px]" aria-label={`Photographs of ${title}`}>
      {frames.map((photo, index) => (
        <div key={index} className="relative w-full overflow-hidden rounded-md aspect-[4/3] bg-neutral-900">
          <img {...resizedImageProps(photo.url, 828, '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw')} alt={photo.alt || `${title}, photograph ${index + 1}`} className="absolute inset-0 h-full w-full object-contain" />
        </div>
      ))}
    </section>
  );
}
